"use client";

/**
 * Bearer-authenticated image: <img> can't send Authorization headers, so we
 * fetch the bytes with the session token and render a blob URL. A module-level
 * cache keeps editor + preview + student view from refetching the same asset.
 */

import { useEffect, useState } from "react";
import { authFetch } from "@/lib/api";
import { useDemo } from "@/lib/demo/DemoContext";

const objectUrlCache = new Map<string, string>();

async function fetchBlobUrl(path: string): Promise<string> {
  const cached = objectUrlCache.get(path);
  if (cached) return cached;
  const response = await authFetch(path);
  if (!response.ok) throw new Error(`asset ${response.status}`);
  const url = URL.createObjectURL(await response.blob());
  objectUrlCache.set(path, url);
  return url;
}

export default function AuthImage({
  courseId,
  assetId,
  alt,
  className,
}: {
  courseId: string;
  assetId: string;
  alt: string;
  className?: string;
}) {
  const demo = useDemo();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (demo) return; // demo assets are plain /public URLs — nothing to fetch
    let cancelled = false;
    setFailed(false);
    fetchBlobUrl(`/api/v1/courses/${courseId}/assets/${assetId}`)
      .then((url) => !cancelled && setSrc(url))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [courseId, assetId, demo]);

  if (demo) {
    const asset = demo.assets[assetId];
    if (!asset) {
      return (
        <span className="block rounded border border-dashed border-line-strong px-4 py-8 text-center text-xs text-faint">
          Image unavailable
        </span>
      );
    }
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={asset.src} alt={alt} className={className ?? "mx-auto rounded-lg"} />;
  }

  if (failed) {
    return (
      <span className="block rounded border border-dashed border-line-strong px-4 py-8 text-center text-xs text-faint">
        Image unavailable
      </span>
    );
  }
  if (!src) return <span className="skeleton block h-48 w-full" />;
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt={alt} className={className ?? "mx-auto rounded-lg"} />;
}
